// MARCA | 24/08/2026 | PDP testimonial carousel — pagination dots, chevrons, optional autoscroll
if (!customElements.get("product-testimonials")) {
  class ProductTestimonials extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.track = this.querySelector(".product-testimonials__track");
      this.slides = Array.from(this.querySelectorAll(".product-testimonials__slide"));
      this.dots = Array.from(this.querySelectorAll(".product-testimonials__dot"));
      this.headings = Array.from(
        this.querySelectorAll(".product-testimonials__heading"),
      );
      this.prevButton = this.querySelector('[data-direction="prev"]');
      this.nextButton = this.querySelector('[data-direction="next"]');
      this.current = 0;
      this.autorotate = this.dataset.autorotate === "true";
      this.intervalMs = parseInt(this.dataset.interval, 10) || 5000;
      this.reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      this.timer = null;
      this.pointerStartX = null;

      this.equalizeHeadings();
      this.resizeObserver = new ResizeObserver(() => this.equalizeHeadings());
      this.resizeObserver.observe(this);

      if (this.slides.length < 2) return;

      this.prevButton?.addEventListener("click", () => this.goTo(this.current - 1, true));
      this.nextButton?.addEventListener("click", () => this.goTo(this.current + 1, true));
      this.dots.forEach((dot) => {
        dot.addEventListener("click", () => {
          const index = parseInt(dot.dataset.slideIndex, 10);
          if (!Number.isNaN(index)) this.goTo(index, true);
        });
      });

      this.addEventListener("keydown", (event) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          this.goTo(this.current - 1, true);
        }
        if (event.key === "ArrowRight") {
          event.preventDefault();
          this.goTo(this.current + 1, true);
        }
      });

      this.addEventListener("pointerdown", (event) => {
        if (event.target.closest("button")) {
          this.pointerStartX = null;
          return;
        }
        this.pointerStartX = event.clientX;
      });
      this.addEventListener("pointerup", (event) => {
        if (this.pointerStartX == null) return;
        const delta = event.clientX - this.pointerStartX;
        this.pointerStartX = null;
        if (Math.abs(delta) < 40) return;
        this.goTo(delta < 0 ? this.current + 1 : this.current - 1, true);
      });

      this.addEventListener("mouseenter", () => this.pause());
      this.addEventListener("mouseleave", () => this.play());
      this.addEventListener("focusin", () => this.pause());
      this.addEventListener("focusout", () => {
        if (!this.contains(document.activeElement)) this.play();
      });

      document.addEventListener("visibilitychange", this.onVisibilityChange);

      this.goTo(0, false);
      this.play();
    }

    disconnectedCallback() {
      this.pause();
      document.removeEventListener("visibilitychange", this.onVisibilityChange);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    equalizeHeadings() {
      if (this._equalizing || !this.headings.length) return;

      this._equalizing = true;
      this.style.removeProperty("--pt-heading-height");

      let maxHeight = 0;
      this.headings.forEach((heading) => {
        maxHeight = Math.max(maxHeight, heading.offsetHeight);
      });

      if (maxHeight > 0) {
        this.style.setProperty("--pt-heading-height", maxHeight + "px");
      }

      requestAnimationFrame(() => {
        this._equalizing = false;
      });
    }

    onVisibilityChange = () => {
      if (document.hidden) {
        this.pause();
      } else {
        this.play();
      }
    };

    goTo(index, userInitiated) {
      if (!this.slides.length) return;

      const total = this.slides.length;
      this.current = ((index % total) + total) % total;
      this.track.style.transform = "translate3d(-" + this.current * 100 + "%, 0, 0)";

      this.slides.forEach((slide, i) => {
        const isActive = i === this.current;
        slide.classList.toggle("is-active", isActive);
        slide.setAttribute("aria-hidden", isActive ? "false" : "true");
      });

      this.dots.forEach((dot, i) => {
        const isActive = i === this.current;
        dot.classList.toggle("is-active", isActive);
        if (isActive) {
          dot.setAttribute("aria-current", "true");
        } else {
          dot.removeAttribute("aria-current");
        }
      });

      if (userInitiated) {
        this.restart();
      }
    }

    play() {
      if (!this.autorotate || this.reduceMotion || this.slides.length < 2 || document.hidden) {
        return;
      }
      this.pause();
      this.timer = setInterval(() => this.goTo(this.current + 1, false), this.intervalMs);
    }

    pause() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    restart() {
      this.pause();
      this.play();
    }
  }

  customElements.define("product-testimonials", ProductTestimonials);
}
